import { useState } from "react";
import { message } from "antd";
import { MakeApiCall } from "../apis";
import { exportProductTemplate } from "../utils/ExportProducttemplate";

/**
 * Hook that fetches all products (no_pagination=true) and exports
 * them into the pre-formatted Excel template — non-blocking via a
 * background message toast so the user can keep using the app.
 *
 * Usage:
 *   const { downloadTemplate, templateLoading } = useProductTemplateExport(userData, selectedMarketplace);
 *   <button onClick={downloadTemplate} disabled={templateLoading}>Template</button>
 */
export const useProductTemplateExport = (userData, selectedMarketplace) => {
    const [templateLoading, setTemplateLoading] = useState(false);

    const downloadTemplate = async () => {
        if (templateLoading) return; // prevent double-click

        setTemplateLoading(true);

        // Show a persistent non-blocking toast — user can keep working
        const loadingKey = "template-export";
        message.loading({
            content: "Preparing template, this may take a moment…",
            key: loadingKey,
            duration: 0, // stays until we dismiss it
        });

        try {
            const response = await MakeApiCall(
                `get-products?no_pagination=true${
                    selectedMarketplace?.id ? `&marketplace_id=${selectedMarketplace.id}` : ""
                }`,
                "get",
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                const records = response?.data?.records || [];

                if (records.length === 0) {
                    message.warning({ content: "No products found to export.", key: loadingKey });
                    return;
                }

                await exportProductTemplate(records, "Product_Import_Template.xlsx");

                message.success({
                    content: `Template ready — ${records.length} product(s) exported.`,
                    key: loadingKey,
                    duration: 4,
                });
            } else {
                message.error({
                    content: response?.message || "Failed to fetch products.",
                    key: loadingKey,
                    duration: 4,
                });
            }
        } catch (err) {
            console.error("Template export error:", err);
            message.error({
                content: "Something went wrong while preparing the template.",
                key: loadingKey,
                duration: 4,
            });
        } finally {
            setTemplateLoading(false);
        }
    };

    return { downloadTemplate, templateLoading };
};